import { GetStaticPaths, GetStaticProps } from 'next';
import {
  NavigationItemFragment,
  PageDetailFragment,
  TournamentCategoryItemFragment,
} from '@amf/shared/types/graphql';
import { sdk } from '@amf/shared/lib/graphQlClient';
import {
  getNavigation,
  getPartners,
  NavigationProps,
  NavigationType,
} from '@amf/shared/utils/navigation';
import { getContentDescription, getContentImageUrl } from '@amf/shared/utils/content';
import Container from '@amf/shared/components/layout/Container';
import Content from '@amf/shared/components/content/Content';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { useTranslate } from '@tolgee/react';
import Link from 'next/link';
import Button from '@amf/shared/components/button/Button';
import PageHeader from '@amf/shared/components/page/Header';
import React from 'react';

import Meta from 'components/Meta';
import Layout from 'components/layout/Layout';
import JoinPlayerSend from 'components/join/JoinPlayerSend';
import JoinRefereeSend from 'components/join/JoinRefereeSend';
import JoinTeamSend from 'components/join/JoinTeamSend';
import { AppLinks } from 'utils/links';

interface StaticPageProps extends NavigationProps {
  page: PageDetailFragment;
  categories: TournamentCategoryItemFragment[];
  joinPlayer?: boolean;
  joinReferee?: boolean;
  joinTeam?: boolean;
}

export default function StaticPage({
  page,
  navigation,
  partners,
  joinPlayer,
  joinReferee,
  joinTeam,
  categories,
}: StaticPageProps) {
  const { t } = useTranslate();
  const imageUrl = getContentImageUrl(page.content);
  const description = getContentDescription(page.content);
  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta title={page.title} image={imageUrl} description={description} />
      <Container>
        <PageHeader title={page.title}>
          {joinPlayer && (
            <Link href={AppLinks.contracts} passHref>
              <a>
                <Button variant='dark-outline'>{t('contracts.title')}</Button>
              </a>
            </Link>
          )}
        </PageHeader>
        <Content content={page.content} />
        {joinPlayer && <JoinPlayerSend />}
        {joinReferee && <JoinRefereeSend />}
        {joinTeam && <JoinTeamSend categories={categories} />}
      </Container>
    </Layout>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  let paths: { params: { path: string[] }; locale: string }[] = [];

  for (const locale of locales ?? []) {
    const navigation = await getNavigation(getLanguageLocale(locale));
    navigation?.forEach(item => {
      if (item.type === NavigationType.link) {
        return;
      }

      if (item.type === NavigationType.page) {
        paths.push({ params: { path: [item.path] }, locale });
        return;
      }

      for (const itemChild of item.children ?? []) {
        if (!itemChild) {
          continue;
        }

        if (itemChild.type === NavigationType.link) {
          continue;
        }

        paths.push({ params: { path: itemChild.path.split('/') }, locale });
      }
    });
  }

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { path } = params as { path: string[] };
  const slug = path.slice(-1).pop();
  if (!slug) {
    return {
      notFound: true,
    };
  }

  let navigation: NavigationItemFragment[];
  let page: PageDetailFragment | null | undefined;
  try {
    const data = await sdk.StaticPagePage({
      union: process.env.NEXT_PUBLIC_UNION,
      locale: getLanguageLocale(locale ?? 'cz'),
      path: slug,
    });
    navigation = data.navigation;
    page = data.page;
  } catch {
    return {
      notFound: true,
    };
  }

  if (!page) {
    return {
      notFound: true,
    };
  }

  const { tournamentCategoryList } = await sdk.TournamentCategoryList({
    union: process.env.NEXT_PUBLIC_UNION,
  });

  return {
    props: {
      navigation,
      page,
      categories: tournamentCategoryList,
      joinPlayer: slug === process.env.NEXT_PUBLIC_JOIN_PLAYER_SLUG,
      joinReferee: slug === process.env.NEXT_PUBLIC_JOIN_REFEREE_SLUG,
      joinTeam: slug === process.env.NEXT_PUBLIC_JOIN_TEAM_SLUG,
      partners: await getPartners(),
    },
    revalidate: 10,
  };
};
